// apps/accounts/src/pages/WorkCategories.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import pb from '../lib/pocketbaseClient';
import toast from 'react-hot-toast';

// Unit options
const unitOptions = [
  { value: 'sqm', label: 'Square Meter (sqm)' },
  { value: 'sqft', label: 'Square Feet (sqft)' },
  { value: 'cum', label: 'Cubic Meter (cum)' },
  { value: 'cft', label: 'Cubic Feet (cft)' },
  { value: 'rmt', label: 'Running Meter (rmt)' },
  { value: 'rft', label: 'Running Feet (rft)' },
];

export default function WorkCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    unit: 'sqm',
    description: '',
    defaultClientRate: '',
    defaultVendorRate: '',
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const records = await pb.collection('work_categories').getFullList({ sort: 'name' });
      console.log('Fetched categories:', records); // Debug log
      setCategories(records);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const data = {
        name: formData.name,
        unit: formData.unit,
        description: formData.description || '',
        defaultClientRate: parseFloat(formData.defaultClientRate) || 0,
        defaultVendorRate: parseFloat(formData.defaultVendorRate) || 0,
      };

      console.log('Submitting data:', data); // Debug log

      if (editing) {
        await pb.collection('work_categories').update(editing.id, data);
        toast.success('Category updated successfully');
      } else {
        await pb.collection('work_categories').create(data);
        toast.success('Category created successfully');
      }
      handleClose();
      fetchCategories();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save category');
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await pb.collection('work_categories').delete(id);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleEdit = (category) => {
    console.log('Editing category:', category); // Debug log
    setEditing(category);
    setFormData({
      name: category.name || '',
      unit: category.unit || 'sqm',
      description: category.description || '',
      defaultClientRate: category.defaultClientRate || '',
      defaultVendorRate: category.defaultVendorRate || '',
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({
      name: '',
      unit: 'sqm',
      description: '',
      defaultClientRate: '',
      defaultVendorRate: '',
    });
    setFormLoading(false);
  };

  // Get unit label
  const getUnitLabel = (unit) => {
    const found = unitOptions.find(u => u.value === unit);
    return found ? found.label : unit;
  };

  // Get unit symbol
  const getUnitSymbol = (unit) => {
    const found = unitOptions.find(u => u.value === unit);
    return found ? found.value : unit;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Work Categories</h1>
          <p className="text-sm text-slate-500">Manage work categories with default rates</p>
        </div>
        <Button 
          className="bg-amber-500 hover:bg-amber-600 text-white"
          onClick={() => setShowModal(true)}
        >
          New Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-500">
            No categories found. Create your first work category!
          </div>
        ) : (
          categories.map(cat => {
            const unitDisplay = cat.unit || 'N/A';
            const unitLabel = getUnitLabel(unitDisplay);
            
            return (
              <Card key={cat.id} className="hover:shadow-md transition-shadow border-l-4 border-l-amber-400">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 text-lg">{cat.name}</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {/* Unit Badge */}
                        <Badge className="bg-blue-100 text-blue-700 border-0">
                          {unitLabel}
                        </Badge>
                        
                        {/* Client Rate */}
                        {cat.defaultClientRate > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0">
                            Client: Rs.{cat.defaultClientRate}/{unitDisplay}
                          </Badge>
                        )}
                        
                        {/* Vendor Rate */}
                        {cat.defaultVendorRate > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 border-0">
                            Vendor: Rs.{cat.defaultVendorRate}/{unitDisplay}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Description */}
                      {cat.description && (
                        <p className="text-sm text-slate-500 mt-2">{cat.description}</p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-1 ml-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        onClick={() => handleEdit(cat)}
                      >
                        Edit
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(cat.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              {editing ? 'Edit Category' : 'New Category'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  required 
                  placeholder="e.g., Plaster Work"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Unit <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({...formData, unit: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                  required
                >
                  {unitOptions.map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Default Client Rate (Rs.)
                  </label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={formData.defaultClientRate} 
                    onChange={(e) => setFormData({...formData, defaultClientRate: e.target.value})} 
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Default Vendor Rate (Rs.)
                  </label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={formData.defaultVendorRate} 
                    onChange={(e) => setFormData({...formData, defaultVendorRate: e.target.value})} 
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description
                </label>
                <Input 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                  placeholder="Optional description..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editing ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}